import { useState } from "react";
import { CalendarPlus, Download, ExternalLink } from "lucide-react";

interface AddToCalendarProps {
  eventId: string;
  title: string;
  description?: string;
  startTime: string;
  endTime?: string;
  location?: string;
  isVirtual?: boolean;
  meetingLink?: string;
}

/** Google Calendar wants UTC basic format: 20260827T063800Z */
function toGoogleDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

export default function AddToCalendar({
  eventId,
  title,
  description,
  startTime,
  endTime,
  location,
  isVirtual,
  meetingLink,
}: AddToCalendarProps) {
  const [open, setOpen] = useState(false);

  const start = toGoogleDate(startTime);
  if (!start) return null;

  // Default to an hour when the event has no end time, matching the .ics export.
  const end =
    (endTime && toGoogleDate(endTime)) ||
    toGoogleDate(new Date(new Date(startTime).getTime() + 60 * 60 * 1000).toISOString());

  const where = isVirtual ? meetingLink || "Online" : location || "To be announced";

  const googleUrl =
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${encodeURIComponent(title)}` +
    `&dates=${start}/${end}` +
    `&details=${encodeURIComponent(description || "")}` +
    `&location=${encodeURIComponent(where)}`;

  const outlookUrl =
    "https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent" +
    `&subject=${encodeURIComponent(title)}` +
    `&startdt=${encodeURIComponent(new Date(startTime).toISOString())}` +
    `&enddt=${encodeURIComponent(
      new Date(endTime || new Date(startTime).getTime() + 60 * 60 * 1000).toISOString()
    )}` +
    `&body=${encodeURIComponent(description || "")}` +
    `&location=${encodeURIComponent(where)}`;

  const linkClass =
    "flex items-center gap-2 px-4 py-2 text-sm text-secondary hover:bg-gray-50 transition-colors";

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-secondary transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <CalendarPlus className="h-4 w-4" />
        Add to calendar
      </button>

      {open && (
        <>
          {/* Click-away layer */}
          <div
            className="fixed inset-0 z-10"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-md border border-border bg-white shadow-lg"
          >
            <a
              role="menuitem"
              href={`/api/events/${eventId}/calendar.ics`}
              className={linkClass}
              onClick={() => setOpen(false)}
            >
              <Download className="h-4 w-4" />
              Apple / Outlook (.ics)
            </a>
            <a
              role="menuitem"
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
              onClick={() => setOpen(false)}
            >
              <ExternalLink className="h-4 w-4" />
              Google Calendar
            </a>
            <a
              role="menuitem"
              href={outlookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
              onClick={() => setOpen(false)}
            >
              <ExternalLink className="h-4 w-4" />
              Outlook Web
            </a>
          </div>
        </>
      )}
    </div>
  );
}
